'use client';

import type { QueryClient } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, loggerLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import { useState } from 'react';
import SuperJSON from 'superjson';
import { env } from '~/env';
import type { AppRouter } from '../../server/src/routers';
import { createQueryClient } from './query-client';

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
    if (typeof window === 'undefined') {
        // Server: always make a new query client
        return createQueryClient();
    }

    // Browser: use singleton pattern to keep the same query client
    // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
    return (clientQueryClientSingleton ??= createQueryClient());
};

export const api = createTRPCReact<AppRouter>();

export const { useTRPC, useTRPCClient, TRPCProvider } = createTRPCContext<AppRouter>();

export function TRPCReactProvider(props: { children: React.ReactNode; cookies: string }) {
    const queryClient = getQueryClient();

    const [trpcClient] = useState(() =>
        createTRPCClient<AppRouter>({
            links: [
                loggerLink({
                    enabled: op =>
                        env.NODE_ENV === 'development' || (op.direction === 'down' && op.result instanceof Error),
                }),
                httpBatchLink({
                    transformer: SuperJSON,
                    url: `${env.NEXT_PUBLIC_SERVER_URL}/trpc`,
                    headers() {
                        const h: Record<string, string> = {};
                        if (typeof window === 'undefined') {
                            h.cookie = props.cookies ?? '';
                        }
                        return h;
                    },
                    fetch(url, options) {
                        return fetch(url, {
                            ...options,
                            credentials: 'include',
                        });
                    },
                }),
            ],
        }),
    );

    return (
        <QueryClientProvider client={queryClient}>
            <api.Provider client={trpcClient} queryClient={queryClient}>
                {props.children}
            </api.Provider>
        </QueryClientProvider>
    );
}
